import React, { Component } from 'react';
import { motion } from 'framer-motion';
import { XIcon, MapPinIcon, UsersIcon, CalendarIcon, TrendingUpIcon, FileTextIcon, DollarSignIcon, GlobeIcon, AwardIcon, BriefcaseIcon, PackageIcon, TagIcon, ExternalLinkIcon, HashIcon, ShieldCheckIcon } from 'lucide-react';
import { Company } from '../types/company';
import { CompanyLogo } from './CompanyLogo';
type CompanyDetailProps = {
  company: Company;
  onClose: () => void;
};
export function CompanyDetail({
  company,
  onClose
}: CompanyDetailProps) {
  return <motion.div initial={{
    opacity: 0
  }} animate={{
    opacity: 1
  }} exit={{
    opacity: 0
  }} className="fixed inset-0 bg-slate-950/80 backdrop-blur-sm z-50 flex items-start justify-end" onClick={onClose} data-id="element-41">
      <motion.div initial={{
      x: '100%'
    }} animate={{
      x: 0
    }} exit={{
      x: '100%'
    }} transition={{
      type: 'spring',
      damping: 30,
      stiffness: 300
    }} onClick={e => e.stopPropagation()} className="w-full max-w-2xl h-full bg-slate-900 border-l border-slate-800 overflow-y-auto" data-id="element-42">
        {/* Header */}
        <div className="sticky top-0 bg-slate-900/95 backdrop-blur-sm border-b border-slate-800 p-6 z-10" data-id="element-43">
          <div className="flex items-start justify-between" data-id="element-44">
            <div className="flex items-center gap-4" data-id="element-45">
              <CompanyLogo website={company.website} name={company.name} size="lg" data-id="element-46" />
              <div data-id="element-47">
                <h2 className="text-2xl font-bold text-white" data-id="element-48">
                  {company.name}
                </h2>
                <p className="text-slate-400" data-id="element-49">{company.nameChinese}</p>
              </div>
            </div>
            <button onClick={onClose} className="p-2 text-slate-400 hover:text-white hover:bg-slate-800 rounded-lg transition-colors" aria-label="Close" data-id="element-50">
              <XIcon className="w-5 h-5" data-id="element-51" />
            </button>
          </div>

          <div className="flex items-center gap-3 mt-4" data-id="element-52">
            <div className="px-3 py-1.5 bg-amber-500/10 border border-amber-500/20 rounded-full" data-id="element-53">
              <span className="text-sm font-semibold text-amber-400" data-id="element-54">
                Innovation Score: {company.innovationScore}/100
              </span>
            </div>
            <span className="px-3 py-1.5 bg-slate-800 text-slate-300 rounded-full text-sm" data-id="element-55">
              {company.industry}
            </span>
            <span className="px-3 py-1.5 bg-slate-800 text-slate-300 rounded-full text-sm" data-id="element-56">
              {company.subIndustry}
            </span>
          </div>
        </div>

        {/* Content */}
        <div className="p-6 space-y-8" data-id="element-57">
          {/* Industry Code */}
          <section data-id="element-58">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-id="element-59">
              <div className="flex items-center gap-3 bg-slate-800/30 border border-slate-700/50 rounded-xl p-4" data-id="element-60">
                <div className="p-2 bg-amber-500/10 rounded-lg" data-id="element-61">
                  <HashIcon className="w-4 h-4 text-amber-500" data-id="element-62" />
                </div>
                <div data-id="element-63">
                  <p className="text-xs text-slate-500 uppercase tracking-wider" data-id="element-64">
                    Industry Code
                  </p>
                  <p className="text-sm font-mono font-semibold text-amber-400" data-id="element-65">
                    {company.industryCode}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-3 bg-slate-800/30 border border-slate-700/50 rounded-xl p-4" data-id="element-66">
                <div className="p-2 bg-emerald-500/10 rounded-lg" data-id="element-67">
                  <ShieldCheckIcon className="w-4 h-4 text-emerald-500" data-id="element-68" />
                </div>
                <div data-id="element-69">
                  <p className="text-xs text-slate-500 uppercase tracking-wider" data-id="element-70">
                    Registration Status
                  </p>
                  <p className="text-sm font-semibold text-emerald-400" data-id="element-71">
                    Existing (In Operation)
                  </p>
                </div>
              </div>
            </div>
          </section>

          {/* Description */}
          <section data-id="element-72">
            <p className="text-slate-300 leading-relaxed" data-id="element-73">
              {company.description}
            </p>
          </section>

          {/* Key Metrics */}
          <section data-id="element-74">
            <h3 className="text-sm font-medium text-slate-500 uppercase tracking-wider mb-4" data-id="element-75">
              Key Metrics
            </h3>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4" data-id="element-76">
              <a href={`https://www.google.com/maps/search/${encodeURIComponent(company.name + ', ' + company.city + ', ' + company.province + ', China')}`} target="_blank" rel="noopener noreferrer" className="bg-slate-800/30 border border-slate-700/50 hover:border-amber-500/30 rounded-xl p-4 transition-colors group cursor-pointer" data-id="element-77">
                <div className="flex items-center gap-2 text-slate-500 mb-1" data-id="element-78">
                  <MapPinIcon className="w-4 h-4 group-hover:text-amber-500 transition-colors" data-id="element-79" />
                  <span className="text-xs uppercase tracking-wider" data-id="element-80">
                    Location
                  </span>
                  <ExternalLinkIcon className="w-3 h-3 ml-auto opacity-0 group-hover:opacity-100 text-amber-500 transition-opacity" data-id="element-81" />
                </div>
                <p className="text-lg font-semibold text-white group-hover:text-amber-400 transition-colors" data-id="element-82">
                  {company.city}, {company.province}
                </p>
              </a>
              <MetricCard icon={<UsersIcon className="w-4 h-4" data-id="element-84" />} label="Employees" value={company.employees.toLocaleString()} data-id="element-83" />
              <MetricCard icon={<CalendarIcon className="w-4 h-4" data-id="element-86" />} label="Founded" value={company.founded.toString()} data-id="element-85" />
              <MetricCard icon={<TrendingUpIcon className="w-4 h-4" data-id="element-88" />} label="Revenue" value={company.revenue} data-id="element-87" />
              <MetricCard icon={<FileTextIcon className="w-4 h-4" data-id="element-90" />} label="Patents" value={company.patents.toLocaleString()} data-id="element-89" />
              <MetricCard icon={<DollarSignIcon className="w-4 h-4" data-id="element-92" />} label="Total Funding" value={company.totalFunding} data-id="element-91" />
            </div>
          </section>

          {/* Funding */}
          <section data-id="element-93">
            <h3 className="text-sm font-medium text-slate-500 uppercase tracking-wider mb-4" data-id="element-94">
              Funding Status
            </h3>
            <div className="bg-slate-800/30 border border-slate-700/50 rounded-xl p-4" data-id="element-95">
              <div className="flex items-center justify-between" data-id="element-96">
                <div data-id="element-97">
                  <p className="text-slate-400 text-sm" data-id="element-98">Current Round</p>
                  <p className="text-xl font-semibold text-white" data-id="element-99">
                    {company.fundingRound}
                  </p>
                </div>
                <div className="text-right" data-id="element-100">
                  <p className="text-slate-400 text-sm" data-id="element-101">Total Raised</p>
                  <p className="text-xl font-semibold text-amber-400" data-id="element-102">
                    {company.totalFunding}
                  </p>
                </div>
              </div>
            </div>
          </section>

          {/* Key Products */}
          <section data-id="element-103">
            <h3 className="text-sm font-medium text-slate-500 uppercase tracking-wider mb-4 flex items-center gap-2" data-id="element-104">
              <PackageIcon className="w-4 h-4" data-id="element-105" />
              Key Products & Services
            </h3>
            <div className="flex flex-wrap gap-2" data-id="element-106">
              {company.keyProducts.map((product, index) => <span key={index} className="px-3 py-2 bg-slate-800/50 border border-slate-700/50 text-slate-300 rounded-lg text-sm" data-id="element-107">
                  {product}
                </span>)}
            </div>
          </section>

          {/* Leadership */}
          <section data-id="element-108">
            <h3 className="text-sm font-medium text-slate-500 uppercase tracking-wider mb-4 flex items-center gap-2" data-id="element-109">
              <BriefcaseIcon className="w-4 h-4" data-id="element-110" />
              Leadership
            </h3>
            <div className="space-y-3" data-id="element-111">
              {company.leadership.map((leader, index) => <div key={index} className="flex items-center gap-3 bg-slate-800/30 border border-slate-700/50 rounded-lg p-3" data-id="element-112">
                  <div className="w-10 h-10 bg-slate-700 rounded-full flex items-center justify-center text-slate-400" data-id="element-113">
                    {leader.name.charAt(0)}
                  </div>
                  <div data-id="element-114">
                    <p className="text-white font-medium" data-id="element-115">{leader.name}</p>
                    <p className="text-slate-400 text-sm" data-id="element-116">{leader.title}</p>
                  </div>
                </div>)}
            </div>
          </section>

          {/* Tags */}
          <section data-id="element-117">
            <h3 className="text-sm font-medium text-slate-500 uppercase tracking-wider mb-4 flex items-center gap-2" data-id="element-118">
              <TagIcon className="w-4 h-4" data-id="element-119" />
              Tags
            </h3>
            <div className="flex flex-wrap gap-2" data-id="element-120">
              {company.tags.map((tag, index) => <span key={index} className="px-3 py-1.5 bg-amber-500/10 border border-amber-500/20 text-amber-400 rounded-full text-sm" data-id="element-121">
                  {tag}
                </span>)}
            </div>
          </section>

          {/* Website */}
          <section data-id="element-122">
            <a href={`https://${company.website}`} target="_blank" rel="noopener noreferrer" className="flex items-center justify-center gap-2 w-full py-3 bg-amber-500 hover:bg-amber-400 text-slate-900 font-semibold rounded-xl transition-colors" data-id="element-123">
              <GlobeIcon className="w-5 h-5" data-id="element-124" />
              Visit Website
              <ExternalLinkIcon className="w-4 h-4" data-id="element-125" />
            </a>
          </section>
        </div>
      </motion.div>
    </motion.div>;
}
function MetricCard({
  icon,
  label,
  value
}: {
  icon: React.ReactNode;
  label: string;
  value: string;
}) {
  return <div className="bg-slate-800/30 border border-slate-700/50 rounded-xl p-4" data-id="element-126">
      <div className="flex items-center gap-2 text-slate-500 mb-1" data-id="element-127">
        {icon}
        <span className="text-xs uppercase tracking-wider" data-id="element-128">{label}</span>
      </div>
      <p className="text-lg font-semibold text-white" data-id="element-129">{value}</p>
    </div>;
}